/**
 * ONE list of the question and section fields the CRF Builder page carries
 * through read-back, its working copy and its exports (D2, 2026-09-16).
 *
 * The page's core mapping (name, text, answer type, options, unit, codelist)
 * lives where it always has. Everything else a stored question may carry is
 * copied HERE, and `crfCarriedFields.test.ts` compares this list with the
 * server's CrfQuestion so a field added there cannot be dropped by the page —
 * which is how server-written notes were once erased by the next save.
 */

export const CARRIED_QUESTION_FIELDS = [
  // Margin content (content pass, 2026-09-14).
  'guidance',
  'guidanceSource',
  'protocolRef',
  'isParent',
  'indent',
  // From the study's data dictionary (D2, 2026-09-16).
  'helpText',
  'required',
  // The number printed on the source form, split off the text on import
  // (functions/src/crf/questionNumbering.ts, 2026-09-27).
  'printed_number',
  // Where the answer codes came from, by the order of authority
  // (functions/src/crf/crfAuthority.ts, 2026-10-02).
  'codesFrom',
] as const;

export interface CarriedQuestionFields {
  guidance?: string;
  guidanceSource?: string;
  protocolRef?: string;
  isParent?: boolean;
  indent?: number;
  helpText?: string;
  required?: boolean;
  printed_number?: string;
  codesFrom?: string;
}

const text = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';

/** Anything that may hold the carried fields: a stored question or a page variable. */
type MayCarry = {
  guidance?: unknown;
  guidanceSource?: unknown;
  protocolRef?: unknown;
  isParent?: unknown;
  indent?: unknown;
  helpText?: unknown;
  required?: unknown;
  printed_number?: unknown;
  codesFrom?: unknown;
};

/** The carried fields of a stored question or a page variable, only those set. */
export function carriedQuestionFields(src: MayCarry): CarriedQuestionFields {
  const out: CarriedQuestionFields = {};
  if (text(src.guidance)) out.guidance = src.guidance;
  if (text(src.guidanceSource)) out.guidanceSource = src.guidanceSource;
  if (text(src.protocolRef)) out.protocolRef = src.protocolRef;
  if (src.isParent === true) out.isParent = true;
  if (typeof src.indent === 'number' && src.indent > 0) out.indent = src.indent;
  if (text(src.helpText)) out.helpText = src.helpText;
  // Only true: absent means not required, as on the stored form.
  if (src.required === true) out.required = true;
  if (text(src.printed_number)) out.printed_number = src.printed_number;
  if (text(src.codesFrom)) out.codesFrom = src.codesFrom;
  return out;
}

export interface CarriedSectionFields {
  instructions?: string;
  protocolRef?: string;
  repeat?: { max?: number; label?: string };
}

/**
 * A section's carried fields. A repeating BLOCK belongs to a question list; a
 * table section (`kind: 'repeating'`) states its rows with `expectedRows`.
 */
export function carriedSectionFields(s: {
  kind?: string;
  instructions?: string;
  protocolRef?: string;
  repeat?: { max?: number; label?: string };
}): CarriedSectionFields {
  const out: CarriedSectionFields = {};
  if (text(s.instructions)) out.instructions = s.instructions;
  if (text(s.protocolRef)) out.protocolRef = s.protocolRef;
  // Tables (repeating and grid) state their own rows and take no repeating block.
  if (s.kind !== 'repeating' && s.kind !== 'grid' && s.repeat && (s.repeat.max !== undefined || text(s.repeat.label))) {
    out.repeat = { ...s.repeat };
  }
  return out;
}
